import React, { useId, useRef } from 'react'

import { Box } from '@doist/marketist'

import { useGSAP } from '@gsap/react'
import { ContainerTwoColumns } from 'components/container-two-column/container-two-column'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import css from './scroll-section.module.css'

gsap.registerPlugin(ScrollTrigger)

type ScrollSectionProps = {
    children: React.ReactNode
    className?: string
}

const mediaQuery = '(min-width: 769px)'

export function ScrollSection({ children, className }: ScrollSectionProps) {
    const id = useId()
    const $ref = useRef<HTMLDivElement>(null)

    useGSAP(
        () => {
            const matchMediaCTX = gsap.matchMedia($ref)

            matchMediaCTX.add(mediaQuery, () => {
                ScrollTrigger.create({
                    id: `scroll-${id}`,
                    start: 'center center',
                    end: 'center center',
                    trigger: "[data-scrollsection='sticky']",
                    endTrigger: "[data-scrollsection='anchor']",
                    pin: true,
                    scrub: true,
                })
            })
        },
        { scope: $ref },
    )

    return (
        <div ref={$ref} className={css.scrollSection} data-scrollsection="container">
            <ContainerTwoColumns className={className}>{children}</ContainerTwoColumns>
        </div>
    )
}

type SectionProps = {
    children: React.ReactNode
}

ScrollSection.StickySection = function StickySection({ children }: SectionProps) {
    return (
        <div className={css.scrollContainer}>
            <div data-scrollsection="sticky">{children}</div>
        </div>
    )
}

ScrollSection.StaticSection = function StaticSection({ children }: SectionProps) {
    return <ContainerTwoColumns.BodyContainer>{children}</ContainerTwoColumns.BodyContainer>
}

type StickyAnchorProps = {
    children?: React.ReactNode
}

/**
 *
 * Anchor is what will tell GSAP's scrolltrigger to stop pinning the sticky section.
 * Wrap this around the element that becomes that anchor, such as the Blockquote.
 */
ScrollSection.StickyAnchor = function StickyAnchor({ children }: StickyAnchorProps) {
    return <div data-scrollsection="anchor">{children}</div>
}

type TriggerProps = {
    children: React.ReactNode
    onEnter?: () => void
    onEnterBack?: () => void
    onLeave?: () => void
    onLeaveBack?: () => void
}

ScrollSection.Trigger = function Trigger({
    children,
    onEnter,
    onEnterBack,
    onLeave,
    onLeaveBack,
}: TriggerProps) {
    const id = useId()
    const ref = useRef<HTMLDivElement>(null)

    useGSAP(
        () => {
            const matchMediaCTX = gsap.matchMedia(ref)

            matchMediaCTX.add(mediaQuery, () => {
                ScrollTrigger.create({
                    id: `trigger-${id}`,
                    start: 'top center',
                    end: 'bottom center',
                    trigger: ref.current,
                    onEnter() {
                        onEnter?.()
                    },
                    onEnterBack() {
                        onEnterBack?.()
                    },
                    onLeave() {
                        onLeave?.()
                    },
                    onLeaveBack() {
                        onLeaveBack?.()
                    },
                })
            })
        },
        { scope: ref },
    )

    return <Box ref={ref}>{children}</Box>
}
